import { useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { subscribeNewsletter } from "@/lib/api";
import { Mail, ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";

const NewsletterBlock = () => {
  const { t, lang } = useLang();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!email || !email.includes("@") || !firstName.trim() || !lastName.trim()) {
      setStatus("error");
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const res = await subscribeNewsletter({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        language_preference: lang,
      });
      const msg = res?.data?.message;
      setStatus(msg === "already_subscribed" ? "already" : "ok");
      setFirstName(""); setLastName(""); setEmail("");
    } catch (err) {
      setStatus("error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className="relative overflow-hidden bg-[#111827] text-[#F7F7F2]"
    >
      <div className="dl-container py-14 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <div className="font-mono-dl text-[10px] tracking-[0.22em] uppercase text-[#FFD400]">Weekly radar</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">
            {t.sections.newsletter}
          </h2>
          <p className="mt-4 text-[#9ca3af] max-w-md">{t.sections.newsletterDesc}</p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-3" data-testid="newsletter-form" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <input
              data-testid="newsletter-firstname"
              type="text"
              required
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t.sections.newsletterFirstName}
              className="bg-white text-[#111827] placeholder:text-[#6b7280] outline-none px-3 py-3 text-sm border-2 border-[#1F2937] focus:border-[#FFD400] transition-colors"
            />
            <input
              data-testid="newsletter-lastname"
              type="text"
              required
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t.sections.newsletterLastName}
              className="bg-white text-[#111827] placeholder:text-[#6b7280] outline-none px-3 py-3 text-sm border-2 border-[#1F2937] focus:border-[#FFD400] transition-colors"
            />
          </div>
          <div className="flex items-stretch border-2 border-[#FFD400] bg-white">
            <span className="grid place-items-center px-4 bg-[#FFD400] text-[#111827]">
              <Mail size={18} />
            </span>
            <input
              data-testid="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.sections.newsletterEmail}
              className="flex-1 bg-white text-[#111827] placeholder:text-[#6b7280] outline-none px-3 py-3 text-sm"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="newsletter-submit"
              className="bg-[#FFD400] hover:bg-[#facc15] disabled:opacity-60 text-[#111827] px-5 inline-flex items-center gap-2 font-bold text-[13px] transition-colors"
            >
              {busy ? "…" : t.sections.newsletterCta} <ArrowRight size={16} />
            </button>
          </div>
          {status === "ok" && (
            <div data-testid="newsletter-status-ok" className="inline-flex items-center gap-2 text-sm font-medium text-[#FFD400] bg-[#1F2937] px-3 py-2 border border-[#FFD400]/40">
              <CheckCircle2 size={16} /> {t.sections.newsletterOk}
            </div>
          )}
          {status === "already" && (
            <div data-testid="newsletter-status-already" className="inline-flex items-center gap-2 text-sm text-white bg-[#1F2937] px-3 py-2 border border-[#374151]">
              <CheckCircle2 size={16} className="text-[#00A878]" /> {t.sections.newsletterAlready}
            </div>
          )}
          {status === "error" && (
            <div data-testid="newsletter-status-error" className="inline-flex items-center gap-2 text-sm text-white bg-[#7f1d1d] px-3 py-2">
              <AlertCircle size={16} /> {t.sections.newsletterError}
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default NewsletterBlock;
